import React, { useRef, useState, useEffect } from 'react';

// Utility function to generate thumbnail from video at specific timestamp
const generateThumbnailFromVideo = (videoUrl, timestampSeconds = 5) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    
    video.onloadedmetadata = () => {
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Ensure timestamp doesn't exceed video duration
      const seekTime = Math.min(timestampSeconds, video.duration - 1);
      video.currentTime = seekTime;
    };
    
    video.onseeked = () => {
      try {
        // Draw the current frame to canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convert canvas to blob/data URL
        canvas.toBlob((blob) => {
          if (blob) {
            const thumbnailUrl = URL.createObjectURL(blob);
            resolve(thumbnailUrl);
          } else {
            reject(new Error('Failed to create thumbnail blob'));
          }
        }, 'image/jpeg', 0.8);
      } catch (error) {
        reject(error);
      }
    };
    
    video.onerror = () => {
      reject(new Error('Video failed to load'));
    };
    
    video.src = videoUrl;
  });
};

// Component to generate and display thumbnail
const VideoThumbnailGenerator = ({ videoSrc, timestampSeconds = 5, alt, style }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const generateThumbnail = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const thumbnail = await generateThumbnailFromVideo(videoSrc, timestampSeconds);
        setThumbnailUrl(thumbnail);
      } catch (err) {
        console.error('Thumbnail generation failed:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (videoSrc) {
      generateThumbnail();
    }
    
    // Cleanup function to revoke object URL
    return () => {
      if (thumbnailUrl) {
        URL.revokeObjectURL(thumbnailUrl);
      }
    };
  }, [videoSrc, timestampSeconds]);
  
  if (isLoading) {
    return (
      <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0' }}>
        <span>Generating thumbnail...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffebee' }}>
        <span style={{ color: '#c62828', fontSize: '0.8rem' }}>Thumbnail failed</span>
      </div>
    );
  }
  
  return (
    <img 
      src={thumbnailUrl} 
      alt={alt} 
      style={style}
      loading="lazy"
    />
  );
};

// Updated Exercise Card Component with dynamic thumbnails
const ExerciseCardWithDynamicThumbnail = ({ exercise, onPlay, categoryColor, thumbnailTimestamp = 5 }) => (
  <article
    className="video-card"
    style={{
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: '2px solid #e9ecef',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      minWidth: 260,
      scrollSnapAlign: 'start',
      ...(categoryColor && { borderColor: categoryColor + '40' })
    }}
    onClick={() => onPlay(exercise)}
  >
    <div style={{ position: 'relative', aspectRatio: '16 / 9', overflow: 'hidden' }}>
      {/* Dynamic Thumbnail from Video */}
      <VideoThumbnailGenerator
        videoSrc={exercise.videoSrc}
        timestampSeconds={thumbnailTimestamp}
        alt={`${exercise.title} thumbnail`}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          transition: 'transform 0.3s ease'
        }}
      />
      
      {/* Play Overlay */}
      <div className="play-overlay" style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0,
        transition: 'opacity 0.3s ease'
      }}>
        <div 
          className="play-button" 
          style={{
            width: 60,
            height: 60,
            backgroundColor: categoryColor || '#FF9933',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            color: '#ffffff',
            transform: 'scale(0.8)',
            transition: 'transform 0.3s ease',
            border: '2px solid #ffffff'
          }}
        >
          ▶
        </div>
      </div>
      
      {/* Duration Badge */}
      <span style={{
        position: 'absolute',
        bottom: '0.5rem',
        right: '0.5rem',
        backgroundColor: '#000080',
        color: '#ffffff',
        padding: '0.25rem 0.5rem',
        borderRadius: '4px',
        fontSize: '0.8rem',
        fontWeight: 500
      }}>
        {exercise.duration}
      </span>
    </div>

    <div style={{ padding: '1rem' }}>
      <h3 style={{
        fontSize: '1.1rem',
        fontWeight: 600,
        marginBottom: '0.5rem',
        color: '#000080',
        lineHeight: 1.3
      }}>
        {exercise.title}
      </h3>
      <p style={{
        fontSize: '0.95rem',
        color: '#666666',
        lineHeight: 1.5,
        marginBottom: '0.75rem'
      }}>
        {exercise.description}
      </p>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        flexWrap: 'wrap'
      }}>
        <span style={{
          padding: '0.25rem 0.6rem',
          borderRadius: '12px',
          fontSize: '0.8rem',
          fontWeight: 600,
          color: '#ffffff',
          backgroundColor: getDifficultyColor(exercise.difficulty)
        }}>
          {exercise.difficulty}
        </span>
        <span style={{ color: '#666666', fontSize: '0.85rem' }}>
          {exercise.category}
        </span>
      </div>
    </div>
  </article>
);

// Batch thumbnail generator for all exercises
const BatchThumbnailGenerator = ({ exercises, onThumbnailsGenerated }) => {
  const [progress, setProgress] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedThumbnails, setGeneratedThumbnails] = useState({});
  
  const generateAllThumbnails = async () => {
    setIsGenerating(true);
    const thumbnails = {};
    
    for (let i = 0; i < exercises.length; i++) {
      const exercise = exercises[i];
      try {
        const thumbnailUrl = await generateThumbnailFromVideo(exercise.videoSrc, 5);
        thumbnails[exercise.title] = thumbnailUrl;
        setProgress(((i + 1) / exercises.length) * 100);
      } catch (error) {
        console.error(`Failed to generate thumbnail for ${exercise.title}:`, error);
        thumbnails[exercise.title] = null;
      }
    }
    
    setGeneratedThumbnails(thumbnails);
    setIsGenerating(false);
    onThumbnailsGenerated(thumbnails);
  };
  
  return (
    <div style={{ 
      position: 'fixed', 
      top: '50%', 
      left: '50%', 
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'white',
      padding: '2rem',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
      zIndex: 1000
    }}>
      <h3>Generate Video Thumbnails</h3>
      <p>This will generate thumbnails from your video frames (may take a few minutes)</p>
      
      {!isGenerating && (
        <button 
          onClick={generateAllThumbnails}
          style={{
            backgroundColor: '#007aff',
            color: 'white',
            border: 'none',
            padding: '1rem 2rem',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Generate All Thumbnails
        </button>
      )}
      
      {isGenerating && (
        <div>
          <div style={{ 
            width: '100%', 
            height: '20px', 
            backgroundColor: '#f0f0f0', 
            borderRadius: '10px',
            overflow: 'hidden',
            marginBottom: '1rem'
          }}>
            <div 
              style={{ 
                width: `${progress}%`, 
                height: '100%', 
                backgroundColor: '#007aff',
                transition: 'width 0.3s ease'
              }}
            />
          </div>
          <p>Generating thumbnails... {Math.round(progress)}%</p>
        </div>
      )}
    </div>
  );
};

// Helper function for difficulty colors
const getDifficultyColor = (difficulty) => {
  switch (difficulty) {
    case 'Beginner': return '#4CAF50';
    case 'Intermediate': return '#FF9800';
    case 'Advanced': return '#F44336';
    default: return '#9E9E9E';
  }
};

export { 
  VideoThumbnailGenerator, 
  ExerciseCardWithDynamicThumbnail, 
  BatchThumbnailGenerator,
  generateThumbnailFromVideo 
};